if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.onSlideChangedDebounced = debounce(this.onSlideChanged.bind(this), 500);
        this.initMediaIndex();

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', (event) => {
            this.updateMediaIndexFromItem(event.detail.currentElement);
            this.syncMediaIndexPosition();
            if (this.elements.thumbnails) this.onSlideChangedDebounced(event);
          });
        }

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initMediaIndex() {
        this.mediaIndex = this.querySelector('.product-media-index');
        this.mediaIndexStage = this.querySelector('.product-media-stage');
        if (!this.mediaIndex) return;

        this.mediaIndexSection = this.closest('product-info') || this.closest('.shopify-section');
        this.onMediaIndexScroll = () => this.syncMediaIndexPosition();
        window.addEventListener('scroll', this.onMediaIndexScroll, { passive: true });
        window.addEventListener('resize', this.onMediaIndexScroll);

        this.observeMediaItems();
        this.mql.addEventListener('change', () => this.observeMediaItems());

        this.querySelectorAll('.product__media-item img').forEach((img) => {
          if (img.complete) return;
          img.addEventListener('load', () => this.syncMediaIndexPosition(), { once: true });
        });
      }

      getHeaderHeight() {
        const value = getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim();
        const parsed = parseFloat(value);
        return Number.isFinite(parsed) ? parsed : 0;
      }

      getActiveMediaItem(items) {
        const activeItem = this.querySelector('.product__media-item.is-active');
        if (activeItem) return activeItem;

        return (
          items.find((item) => {
            if (item.offsetParent === null && window.getComputedStyle(item).display === 'none') return false;
            const rect = item.getBoundingClientRect();
            return rect.width > 0 && rect.height > 0;
          }) || items[0]
        );
      }

      positionMediaIndexOnActiveItem(items) {
        const activeItem = this.getActiveMediaItem(items);
        if (!activeItem) return;

        const stageRect = this.mediaIndexStage.getBoundingClientRect();
        const itemRect = activeItem.getBoundingClientRect();
        if (itemRect.height === 0) return;

        this.mediaIndex.classList.remove('is-fixed');
        this.mediaIndex.style.left = `${Math.max(0, itemRect.left - stageRect.left)}px`;
        this.mediaIndex.style.top = `${itemRect.top - stageRect.top + itemRect.height / 2}px`;
        this.mediaIndex.style.transform = 'translateY(-50%)';
      }

      getMediaItems() {
        return Array.from(this.querySelectorAll('.product__media-item'));
      }

      isHorizontalSliderActive() {
        const list = this.querySelector('.product__media-list');
        if (!list) return false;
        const overflowX = window.getComputedStyle(list).overflowX;
        return overflowX === 'auto' || overflowX === 'scroll';
      }

      isThumbnailLayout() {
        return (this.dataset.desktopLayout || '').includes('thumbnail') && this.mql.matches;
      }

      syncMediaIndexPosition() {
        if (!this.mediaIndex || !this.mediaIndexStage) return;

        const items = this.getMediaItems();
        const section = this.mediaIndexSection || this.mediaIndexStage;
        const sectionRect = section.getBoundingClientRect();
        const centerY = window.innerHeight / 2;
        const sectionEnded = sectionRect.bottom <= centerY;

        this.mediaIndex.toggleAttribute('hidden', items.length <= 1 || sectionEnded);
        if (sectionEnded || items.length <= 1) return;

        this.updateMediaIndexFromViewport(items, centerY);

        if (!this.mql.matches || this.isHorizontalSliderActive() || this.isThumbnailLayout()) {
          this.positionMediaIndexOnActiveItem(items);
          return;
        }

        const stageRect = this.mediaIndexStage.getBoundingClientRect();
        const headerHeight = this.getHeaderHeight();
        const indexHeight = this.mediaIndex.offsetHeight || 0;
        const minTop = headerHeight + indexHeight / 2 + 8;

        this.mediaIndex.classList.add('is-fixed');
        this.mediaIndex.style.top = `${Math.max(centerY, minTop)}px`;
        this.mediaIndex.style.left = `${stageRect.left}px`;
        this.mediaIndex.style.transform = 'translateY(-50%)';
      }

      updateMediaIndexFromViewport(items, centerY) {
        if (this.isHorizontalSliderActive()) return;

        let closest = 0;
        let closestDist = Infinity;

        items.forEach((item, index) => {
          if (item.offsetParent === null && window.getComputedStyle(item).display === 'none') return;
          const rect = item.getBoundingClientRect();
          if (rect.height === 0) return;
          const dist = Math.abs(rect.top + rect.height / 2 - centerY);
          if (dist < closestDist) {
            closestDist = dist;
            closest = index;
          }
        });

        this.setMediaIndex(closest + 1, items.length);
      }

      observeMediaItems() {
        const items = this.getMediaItems();
        const active = this.querySelector('.product__media-item.is-active') || items[0];
        this.updateMediaIndexFromItem(active);
        this.syncMediaIndexPosition();
      }

      updateMediaIndexFromItem(item) {
        if (!item || !this.mediaIndex) return;
        const items = this.getMediaItems();
        const index = items.indexOf(item);
        if (index === -1) return;
        this.setMediaIndex(index + 1, items.length);
      }

      setMediaIndex(current, total) {
        if (!this.mediaIndex) return;

        const currentEl = this.mediaIndex.querySelector('.product-media-index__current');
        const totalEl = this.mediaIndex.querySelector('.product-media-index__total');
        if (currentEl) currentEl.textContent = String(current);
        if (totalEl) totalEl.textContent = String(total);
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, forceScroll = false) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }

          if (forceScroll) {
            activeMedia.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
          }

          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.observeMediaItems();
        this.updateMediaIndexFromItem(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
